'use client';

import { Button, Divider, Image, Typography } from '@musinsa/mds';
import { useDialog } from '@musinsa/mds-ui';
import { useDeviceInfo } from '@musinsa/packages-device-detection';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { pathsMusinsa } from '../../common';
import { BottomButtonWrapper } from '../../common/components/BottomButtonWrapper';
import * as InfiniteList from '../../common/components/InfiniteList';
import { useCuratorSnackbar } from '../../common/hooks/useCuratorSnackbar';
import { useProposalDetail } from '../hooks/useProposalDetail';
import { useProposalGoods } from '../hooks/useProposalGoods';
import { useRejectProposal } from '../hooks/useProposalMutations';
import {
  isMusinsaService,
  trackCampaignProposalRejectDialogClickMusinsa,
  trackCampaignProposalResponseClickMusinsa,
  trackProposalDetailScreenViewMusinsa,
} from '../utils/campaignProposalMusinsaAnalytics';
import { BrandHeader } from './common/BrandHeader';

interface ProposalDetailPageViewProps {
  campaignProposalId: number;
}

export const ProposalDetailPageView = ({ campaignProposalId }: ProposalDetailPageViewProps) => {
  const router = useRouter();
  const { service } = useDeviceInfo();
  const dialog = useDialog();
  const snackbar = useCuratorSnackbar();
  const { data: detail, isLoading, isError, error } = useProposalDetail(campaignProposalId);
  const {
    data: goodsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProposalGoods(campaignProposalId);
  const { mutate: reject } = useRejectProposal();
  const [isClicked, setIsClicked] = useState(false);

  const goodsItems = goodsData?.pages.flatMap((page) => page.list) ?? [];

  useEffect(() => {
    if (!isMusinsaService(service)) return;
    trackProposalDetailScreenViewMusinsa();
  }, [service, campaignProposalId]);

  if (isLoading) {
    return null;
  }

  if (isError || !detail) {
    const errorMessage = error instanceof Error ? error.message : '제안 정보를 불러올 수 없습니다.';
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <Typography variant="body13pxReg" colorScheme="gray-600">
          {errorMessage}
        </Typography>
        <button type="button" onClick={() => router.back()}>
          <Typography variant="body13pxReg" colorScheme="blue">
            돌아가기
          </Typography>
        </button>
      </div>
    );
  }

  // 상세 화면에서는 '캠페인 ' prefix 없이 노출. 거절 상태는 FE에서 라벨을 보장
  const isRejected = detail.progressStatus.includes('거절');
  const progressLabel = isRejected ? '제안 거절' : detail.progressStatus.replace(/^캠페인\s+/, '');
  const isBlueStatus = progressLabel === '진행중';

  const handleReject = () => {
    if (isClicked) return;
    setIsClicked(true);
    reject(
      { campaignProposalId },
      {
        onSuccess: () => {
          snackbar.show({ message: '쿠폰 캠페인 제안을 거절했습니다.' });
          router.push(
            pathsMusinsa.campaignCollaboration({
              query: { type: 'BRANDS_PROPOSAL', status: 'REJECTED' },
            }),
          );
        },
        onError: () => {
          setIsClicked(false);
        },
      },
    );
  };

  const openRejectDialog = () => {
    if (isMusinsaService(service)) {
      trackCampaignProposalResponseClickMusinsa({
        name: 'reject',
        campaignProposalId,
        brandId: detail.brandId,
      });
    }

    dialog.open({
      title: `${detail.brandName} ${detail.salesRate}% 할인 쿠폰 캠페인 제안을\n거절하시겠습니까?`,
      cancelButtonText: '취소',
      confirmButtonText: '거절하기',
      onCancel: () => {
        if (isMusinsaService(service)) {
          trackCampaignProposalRejectDialogClickMusinsa({
            name: 'cancel',
            campaignProposalId,
            brandId: detail.brandId,
          });
        }
        dialog.close();
      },
      onConfirm: () => {
        if (isMusinsaService(service)) {
          trackCampaignProposalRejectDialogClickMusinsa({
            name: 'reject',
            campaignProposalId,
            brandId: detail.brandId,
          });
        }
        handleReject();
      },
    });
  };

  const onAcceptClick = () => {
    if (isMusinsaService(service)) {
      trackCampaignProposalResponseClickMusinsa({
        name: 'accept',
        campaignProposalId,
        brandId: detail.brandId,
      });
    }
    router.push(pathsMusinsa.campaignCouponSettingName({ query: { id: campaignProposalId } }));
  };

  return (
    <div className="flex flex-col flex-1 bg-white pb-[72px]!">
      {/* 브랜드 정보 + 상태 */}
      <div className="px-4 pt-6">
        <BrandHeader
          imageUrl={detail.brandImageUrl}
          name={detail.brandName}
          statusText={progressLabel}
          statusColorScheme={isBlueStatus ? 'blue' : 'gray-600'}
        />
      </div>

      {/* 제안 정보 — 레거시 ProposalInfo: Padding top/bottom 1, left/right 4 */}
      <div className="flex justify-between px-4 pt-3 pb-1">
        <Typography colorScheme="black" variant="body13pxReg">
          제안날짜
        </Typography>
        <Typography colorScheme="black" variant="body13pxReg">
          {detail.proposalDate}
        </Typography>
      </div>
      <div className="flex justify-between px-4 py-1">
        <Typography colorScheme="black" variant="body13pxReg">
          제안번호
        </Typography>
        <Typography colorScheme="black" variant="body13pxReg">
          {String(detail.campaignProposalId)}
        </Typography>
      </div>
      <div className="flex justify-between px-4 py-1">
        <Typography colorScheme="black" variant="body13pxReg">
          적용상품
        </Typography>
        <Typography
          colorScheme="black"
          variant="body13pxReg"
        >{`${detail.goodsCount}개`}</Typography>
      </div>
      <div className="flex justify-between px-4 py-1">
        <Typography colorScheme="black" variant="body13pxReg">
          할인율 / 쿠폰 발행 수
        </Typography>
        <Typography
          colorScheme="black"
          variant="body13pxReg"
        >{`${detail.salesRate}% / ${detail.couponQuantityLimit.toLocaleString()}건`}</Typography>
      </div>
      <div className="flex justify-between px-4 pt-1 pb-3">
        <Typography colorScheme="black" variant="body13pxReg">
          사용기간
        </Typography>
        <Typography colorScheme="black" variant="body13pxReg">
          {detail.period}
        </Typography>
      </div>

      {/* Divider — 레거시: paddingOptions top 3, bottom 3, left 4, right 4 */}
      <div className="px-4 py-3">
        <Divider variant="line" />
      </div>

      {/* 상품 목록 */}
      <div className="px-4">
        {goodsItems.length === 0 ? (
          <div className="flex justify-center items-center h-[375px]">
            <Typography variant="body14pxMed">
              적용한 상품이 판매 중지 및 품절되었습니다.
            </Typography>
          </div>
        ) : (
          <>
            <div className="pt-3 pb-1">
              <Typography colorScheme="black" variant="body14pxMed">
                적용 상품 {detail.goodsCount}개
              </Typography>
            </div>
            <InfiniteList.Root
              className="box-border relative flex flex-col pb-9"
              loadMore={() => fetchNextPage()}
              isPending={isFetchingNextPage}
              hasMore={hasNextPage}
            >
              {goodsItems.map((goods, index) => {
                const isFirstItem = index === 0;
                const isLastItem = index === goodsItems.length - 1;
                return (
                  <li
                    key={goods.goodsNo}
                    className={`list-none ${isFirstItem ? 'pt-3' : 'pt-2'} ${isLastItem ? 'pb-9' : 'pb-2'}`}
                  >
                    <a href={goods.linkUrl} target="_blank" rel="noreferrer" className="flex gap-3">
                      <div className="w-[72px] h-[86px] rounded-md overflow-hidden shrink-0">
                        <Image src={goods.goodsImageUrl} alt={goods.goodsName} loading="eager" />
                      </div>
                      <div className="flex flex-col flex-1 min-w-0 gap-0.5 mt-[6px]">
                        <Typography colorScheme="black" variant="body13pxSemi" className="truncate">
                          {goods.goodsNo}
                        </Typography>
                        <Typography colorScheme="black" variant="body13pxReg" className="truncate">
                          {goods.goodsName}
                        </Typography>
                        <Typography
                          colorScheme="gray-600"
                          variant="body13pxReg"
                          className="line-through"
                        >
                          {goods.price.toLocaleString()}원
                        </Typography>
                        <div className="flex gap-0.5 relative top-[-2px]">
                          <Typography colorScheme="blue" variant="body13pxSemi">
                            {goods.discountedPrice.toLocaleString()}원
                          </Typography>
                          <Typography colorScheme="blue" variant="body13pxReg">
                            쿠폰 적용가
                          </Typography>
                        </div>
                      </div>
                    </a>
                  </li>
                );
              })}
              <InfiniteList.Trigger />
            </InfiniteList.Root>
          </>
        )}
      </div>

      {/* 하단 액션 버튼 */}
      {detail.isProposalActionVisible && detail.goodsCount > 0 && (
        <BottomButtonWrapper className="py-3 fixed! z-20">
          <div className="flex gap-2">
            <Button variant="secondary" className="w-1/2" onClick={openRejectDialog}>
              거절
            </Button>
            <Button variant="primary" className="w-1/2" onClick={onAcceptClick}>
              제안 수락하기
            </Button>
          </div>
        </BottomButtonWrapper>
      )}
    </div>
  );
};
